import React, { useEffect, useState } from "react";
import { Modal, Radio, Space, Checkbox } from "antd";
import { CopyOutlined, LinkOutlined } from "@ant-design/icons";

const stepTypeLabels = {
  click_on_element: "Click on element",
  click_on_random_element: "Click on random element from list",
  collect_dl_object: "Collect dataLayer object",
  collect_dl_before_navigation: "Collect dataLayer before navigation",
  date_range_select: "Select date range",
  fill_input_field: "Fill input field",
  go_to_page: "Go to page",
  scroll_to_percentage: "Scroll to depth",
  select_option: "Select dropdown menu option",
  wait_until_element_visible: "Wait until element appear",
};

const getStepKey = (step) => step?.stepId || step?.blockId;

const getStepLabel = (step, getBlockDisplayName) => {
  if (step?.blockType === "component_ref") {
    const name =
      (typeof getBlockDisplayName === "function" && getBlockDisplayName(step)) ||
      step.componentName ||
      "Unnamed Component";
    return `Component: ${name}`;
  }
  if (step?.blockType === "scenario_link") {
    const name =
      (typeof getBlockDisplayName === "function" && getBlockDisplayName(step)) ||
      step.sourceScenarioTitle ||
      "Unnamed Scenario";
    return `Live link: ${name}`;
  }
  const label = stepTypeLabels[step?.type] || step?.type || "Unknown";
  const detail = step?.selector || step?.value || "";
  return detail ? `${label} — ${detail}` : label;
};

const ImportStepsModal = ({
  visible = false,
  scenarioTitle = "",
  sourceSteps = [],
  getBlockDisplayName = null,
  onConfirm = () => {},
  onCancel = () => {},
}) => {
  const [importMode, setImportMode] = useState("copy");
  const [selectedStepIds, setSelectedStepIds] = useState([]);

  useEffect(() => {
    if (visible) {
      setImportMode("copy");
      setSelectedStepIds(sourceSteps.map((s) => getStepKey(s)).filter(Boolean));
    }
  }, [visible, sourceSteps]);

  const handleToggleStep = (key) => {
    setSelectedStepIds((prev) =>
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key],
    );
  };

  const allSelected =
    sourceSteps.length > 0 && selectedStepIds.length === sourceSteps.length;

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedStepIds(sourceSteps.map((s) => getStepKey(s)).filter(Boolean));
    } else {
      setSelectedStepIds([]);
    }
  };

  const handleOk = () => {
    if (importMode === "copy") {
      onConfirm("copy", selectedStepIds);
    } else {
      onConfirm("link", null);
    }
    setImportMode("copy");
  };

  const handleCancel = () => {
    onCancel();
    setImportMode("copy");
  };

  const confirmDisabled =
    importMode === "copy" && selectedStepIds.length === 0;

  return (
    <Modal
      centered
      open={visible}
      title={`Import steps from "${scenarioTitle}"`}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Import"
      okType="saveChanges"
      zIndex={2000}
      okButtonProps={{ disabled: confirmDisabled }}
    >
      <div style={{ marginTop: 16 }}>
        <p style={{ marginBottom: 12, fontWeight: 500 }}>
          How would you like to import?
        </p>
        <Radio.Group
          value={importMode}
          onChange={(e) => setImportMode(e.target.value)}
        >
          <Space direction="vertical" size={12}>
            <Radio value="copy">
              <Space align="start" size={8}>
                <CopyOutlined style={{ marginTop: 3 }} />
                <div>
                  <div style={{ fontWeight: 500 }}>Copy (snapshot)</div>
                  <div style={{ color: "#666", fontSize: "0.88em" }}>
                    Steps will be duplicated with new IDs. No ongoing link to
                    the source scenario.
                  </div>
                </div>
              </Space>
            </Radio>
            <Radio value="link">
              <Space align="start" size={8}>
                <LinkOutlined style={{ marginTop: 3 }} />
                <div>
                  <div style={{ fontWeight: 500 }}>Live link</div>
                  <div style={{ color: "#666", fontSize: "0.88em" }}>
                    Steps will stay synced with the source scenario. You can
                    detach later to make them independent.
                  </div>
                </div>
              </Space>
            </Radio>
          </Space>
        </Radio.Group>
      </div>

      {importMode === "copy" && sourceSteps.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <label style={{ fontWeight: 600, display: "block", marginBottom: 8 }}>
            Select steps to copy
          </label>
          <div style={{ marginBottom: 8 }}>
            <Checkbox
              checked={allSelected}
              indeterminate={
                selectedStepIds.length > 0 && !allSelected
              }
              onChange={(e) => handleSelectAll(e.target.checked)}
            >
              Select all
            </Checkbox>
          </div>
          <div
            style={{
              // Only constrain height once the list exceeds 5 steps;
              // shorter lists render fully without an internal scrollbar.
              maxHeight: sourceSteps.length > 5 ? 250 : "none",
              overflowY: sourceSteps.length > 5 ? "auto" : "visible",
              border: "1px solid #f0f0f0",
              borderRadius: 4,
              padding: 8,
            }}
          >
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              {sourceSteps.map((step, idx) => {
                const key = getStepKey(step);
                if (!key) return null;
                return (
                  <Checkbox
                    key={key}
                    checked={selectedStepIds.includes(key)}
                    onChange={() => handleToggleStep(key)}
                  >
                    <span style={{ fontSize: "0.9em" }}>
                      <strong>Step {idx + 1}:</strong>{" "}
                      {getStepLabel(step, getBlockDisplayName)}
                    </span>
                  </Checkbox>
                );
              })}
            </Space>
          </div>
        </div>
      )}
    </Modal>
  );
};

export { ImportStepsModal };
export default ImportStepsModal;
